import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainContainer from "../../components/MainContainer";
import CollectionFilter from "../../components/CollectionFilter/CollectionFilter";
import { useDispatch, useSelector } from "react-redux";
import {
  createFolder,
  deleteCollection,
  deleteFolder,
  getCollections,
  getFolders,
  putCollectionToFolder,
  rateModel,
} from "../../store/slices/model/asyncThunks";
import { useNavigate, useSearchParams } from "react-router-dom";
import debounce from "lodash.debounce";
import {
  selectCollectionFolders,
  selectCollections,
  selectCreateFolderPending,
  selectDeleteCollectionPending,
  selectDeleteFolderPending,
  selectPutCollectionFolderPending,
} from "../../store/slices/model/slice";
import Pagination from "../../components/Pagination/Pagination";
import {
  Button,
  Container,
  Content,
  ContentContainer,
  EmptyState,
  PaginationWrapper,
} from "./CollectionPage.styles";
import CollectionModal from "../../components/CollectionModal/CollectionModal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import CollectionSkeletonLoader from "../../components/utils/CollectionsSkeletonLoader";
import CollectionItem from "../../components/utils/CollectionItem/CollectionItem";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import RateCollectionModal from "../../components/RateCollectionModal/RateCollectionModal";
import { toast } from "sonner";
import CreateFolderModal from "../../components/CreateFolderModal/CreateFolderModal";
import AddToFolderModal from "../../components/AddToFolderModal/AddToFolderModal";
import DeleteFolderModal from "../../components/DeleteFolderModal/DeleteFolderModal";
import CollectionSettingsModal from "../../components/CollectionSettingsModal/CollectionSettingsModal";

const CollectionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const filter = searchParams.get("filter") || "ALL";

  const [searchValue, setSearchValue] = useState(() => search ?? "");
  const loggedIn = useSelector(selectLoggedIn);
  const deleteCollectionPending = useSelector(selectDeleteCollectionPending);
  const { data, loading, total } = useSelector(selectCollections);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [openCollectionModal, setOpenCollectionModal] = useState(false);
  const [selectedCollectionVideo, setSelectedCollectionVideo] = useState(null);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  const [showRateCollectionModal, setShowRateCollectionModal] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isAddToFolderModalOpen, setIsAddToFolderModalOpen] = useState(false);
  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");

  const [selectedDeleteFolder, setSelectedDeleteFolder] = useState(null);

  const { data: folders } = useSelector(selectCollectionFolders);

  const createFolderLoading = useSelector(selectCreateFolderPending);
  const addCollectionToFolderLoading = useSelector(
    selectPutCollectionFolderPending
  );

  const deleteFolderLoading = useSelector(selectDeleteFolderPending);

  const changeHandler = useCallback(
    (text) => {
      searchParams.set("search", text);
      searchParams.set("page", 1);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );

  const handleSetFilter = (filter) => {
    searchParams.set("page", 1);
    searchParams.set("filter", filter);
    setSearchParams(searchParams);
  };

  const setPage = (page) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });

    setTimeout(() => {
      searchParams.set("page", page);
      setSearchParams(searchParams);
    }, 300);
  };

  useEffect(() => {
    if (loggedIn) {
      dispatch(
        getCollections({
          page,
          search,
          filter,
        })
      );
    }
  }, [dispatch, page, search, loggedIn, filter]);

  useEffect(() => {
    if (loggedIn) {
      dispatch(getFolders());
    }
  }, [dispatch, loggedIn]);

  const handleDeleteConfirm = () => {
    if (selectedCollectionVideo) {
      dispatch(deleteCollection(selectedCollectionVideo._id))
        .unwrap()
        .then(() => {
          setShowDeleteConfirm(false);
          setOpenCollectionModal(false);
          setSelectedCollectionVideo(null);
        })
        .catch((err) => {
          console.log(err);
          setShowDeleteConfirm(false);
          toast.error(err.message);
        });
    }
  };

  const handleRateCollection = (rating) => {
    if (selectedCollectionVideo) {
      setSelectedCollectionVideo((prev) => {
        return { ...prev, rating };
      });

      dispatch(rateModel({ collectionId: selectedCollectionVideo._id, rating }))
        .unwrap()
        .then(() => {})
        .catch((err) => {
          console.log(err);
        });

      setTimeout(() => {
        setShowRateCollectionModal(false);
      }, 150);
    }
  };

  const handleCollectionModalClose = () => {
    setOpenCollectionModal(false);
    setSelectedCollectionVideo(null);
  };

  if (loggedIn === null) {
    return null;
  }

  const handleCollectionItemClick = (video) => {
    setSelectedCollectionVideo(video);
    setOpenCollectionModal(true);
  };

  const openCreateFolderModal = () => {
    setIsCreateFolderModalOpen(true);
  };

  const handleCreateFolder = () => {
    if (isCreatingNew) {
      dispatch(createFolder({ name: folderName }))
        .unwrap()
        .then(() => {
          setFolderName("");
          setIsCreateFolderModalOpen(false);
          setIsAddToFolderModalOpen(true);
        })
        .catch((err) => {
          console.log(err);
          toast.error(err.message ?? "Error occurred while creating folder");
        });
    } else {
      dispatch(createFolder({ name: folderName }))
        .unwrap()
        .then(() => {
          setFolderName("");
          setIsCreateFolderModalOpen(false);
        })
        .catch((err) => {
          console.log(err);
          toast.error(err.message ?? "Error occurred while creating folder");
        });
    }
  };

  const handleAddToFolder = ({ folderIds }) => {
    const requestData = {
      collectionId: selectedCollectionVideo._id,
      folderIds,
    };

    dispatch(putCollectionToFolder(requestData))
      .unwrap()
      .then(() => {
        setIsAddToFolderModalOpen(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Error occurred while adding collection to folder");
      });
  };

  const handleDeleteFolderModal = () => {
    dispatch(deleteFolder({ folderId: selectedDeleteFolder._id }))
      .unwrap()
      .then(() => {
        setIsDeleteFolderModalOpen(false);
        setSelectedDeleteFolder(null);
        if (String(selectedDeleteFolder._id) === filter) {
          handleSetFilter("ALL");
        }
      })
      .catch((err) => {
        toast.error("Error occurred while deleting folder");
      });
  };

  const handleCopyPrompt = () => {
    setIsSettingsModalOpen(false);
    if (!customPrompt) return;
    navigator.clipboard
      .writeText(customPrompt)
      .then(() => {
        toast.success("Prompt copied!");
      })
      .catch(() => {
        toast.error("Failed to copy.");
      });
  };

  return (
    <MainContainer offPadding hideTabs={false}>
      <Container>
        <CollectionFilter
          searchPlaceholderText="search for title"
          hideFilter
          showFolders
          folders={folders ?? []}
          setFilter={handleSetFilter}
          filter={filter}
          openCreateFolderModal={openCreateFolderModal}
          hideSearch={!loggedIn}
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
            debouncedChangeHandler(value);
          }}
          openDeleteFolder={() => setIsDeleteFolderModalOpen(true)}
          setSelectedDeleteFolder={setSelectedDeleteFolder}
        />
        <ContentContainer>
          {!loggedIn ? (
            <EmptyState>
              <h3>Login Required</h3>
              <p>You need to be logged in to view your videos.</p>
              <Button onClick={() => dispatch(setSignUpModalOpen(true))}>
                Login
              </Button>
            </EmptyState>
          ) : loading || !data ? (
            <CollectionSkeletonLoader count={12} />
          ) : (
            <Content>
              {!data.length ? (
                <EmptyState>
                  {filter !== "ALL" ? (
                    <>
                      <h3>Your folder is empty</h3>
                      <p>No collections found</p>
                    </>
                  ) : (
                    <>
                      <h3>
                        {search
                          ? "No search results"
                          : "Your collections are empty"}
                      </h3>
                      <p>
                        {search
                          ? `No videos found for "${search}"`
                          : "You haven't created any collections yet."}
                      </p>
                    </>
                  )}

                  {!search && filter === "ALL" && (
                    <Button onClick={() => navigate("/")}>Go Explore</Button>
                  )}
                </EmptyState>
              ) : (
                data.map((item, index) => {
                  return (
                    <CollectionItem
                      key={item._id}
                      video={item}
                      onOpenVideoModal={() =>
                        setIsGenerateCollectionModalOpen(true)
                      }
                      onCollectionItemClick={() =>
                        handleCollectionItemClick(item)
                      }
                      priority={index < 6}
                    />
                  );
                })
              )}
            </Content>
          )}
        </ContentContainer>
        <PaginationWrapper>
          <Pagination
            currentPage={page}
            totalCount={total}
            perPage={20}
            onPageChange={(page) => setPage(page)}
          />
        </PaginationWrapper>
      </Container>

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
      />

      <CollectionModal
        isOpen={openCollectionModal}
        onClose={handleCollectionModalClose}
        video={selectedCollectionVideo}
        onOpenVideoModal={() => setIsGenerateCollectionModalOpen(true)}
        onDeleteClick={() => setShowDeleteConfirm(true)}
        onRateClick={() => setShowRateCollectionModal(true)}
        onFolderClick={() => setIsAddToFolderModalOpen(true)}
        onSettingsClick={() => setIsSettingsModalOpen(true)}
        setCustomPrompt={setCustomPrompt}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Video"
        message="Are you sure you want to delete this video? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={deleteCollectionPending}
        theme="dark"
      />

      <RateCollectionModal
        isOpen={showRateCollectionModal}
        onClose={() => setShowRateCollectionModal(false)}
        onConfirm={handleRateCollection}
        rating={selectedCollectionVideo?.rating ?? 0}
      />

      <CreateFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => setIsCreateFolderModalOpen(false)}
        onConfirm={handleCreateFolder}
        folderName={folderName}
        setFolderName={setFolderName}
        isLoading={createFolderLoading || addCollectionToFolderLoading}
      />

      <AddToFolderModal
        isOpen={isAddToFolderModalOpen}
        onClose={() => setIsAddToFolderModalOpen(false)}
        onConfirm={handleAddToFolder}
        folders={folders}
        selectedCollectionFolders={selectedCollectionVideo?.folders ?? []}
        isLoading={addCollectionToFolderLoading}
        openCreateFolderModal={openCreateFolderModal}
        setIsCreatingNew={setIsCreatingNew}
        isCreatingNew={isCreatingNew}
      />

      <DeleteFolderModal
        isOpen={isDeleteFolderModalOpen}
        onClose={() => setIsDeleteFolderModalOpen(false)}
        onConfirm={handleDeleteFolderModal}
        selectedFolder={selectedDeleteFolder}
        isLoading={deleteFolderLoading}
      />

      <CollectionSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onCopy={handleCopyPrompt}
        customPrompt={customPrompt}
      />
    </MainContainer>
  );
};

export default CollectionPage;
